import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Mail, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import useSEO from "@/lib/seo";

const SKYLINE_URL = "/assets/skyline-baustelle.png";

export default function NewsletterConfirm() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ loading: true, ok: false, message: "", email: "", already: false });
  useSEO({
    title: "Newsletter-Bestätigung",
    description: "Bestätigen Sie Ihre Newsletter-Anmeldung für das Quartier am Hornschen Tor.",
    path: "/newsletter/bestaetigt",
  });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, message: "Es wurde kein Bestätigungs-Token übergeben.", email: "", already: false });
      return;
    }
    api.get(`/newsletter/confirm/${encodeURIComponent(token)}`)
      .then((r) => setState({ loading: false, ok: true, message: r.data.message || "Bestätigt.", email: r.data.email || "", already: !!r.data.already }))
      .catch((e) => setState({ loading: false, ok: false, message: formatApiError(e.response?.data?.detail) || "Bestätigung fehlgeschlagen.", email: "", already: false }));
  }, [token]);

  return (
    <div data-testid="newsletter-confirm-page">
      <section className="relative bg-[hsl(var(--brand-soft))] overflow-hidden">
        <div className="container-narrow pt-16 lg:pt-24 pb-40 lg:pb-52 relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-5">
            <Mail className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-dark))]">Newsletter-Bestätigung</span>
          </div>

          {state.loading ? (
            <>
              <h1 className="font-semibold text-3xl sm:text-4xl mb-3">Einen Moment bitte…</h1>
              <p className="text-base text-neutral-600">Wir prüfen Ihren Bestätigungslink.</p>
            </>
          ) : state.ok ? (
            <>
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-white border-2 border-[hsl(var(--brand-green))] mb-6">
                <CheckCircle2 className="h-7 w-7 text-[hsl(var(--brand-green-strong))]" strokeWidth={1.8} />
              </div>
              <h1 className="font-semibold text-4xl sm:text-5xl leading-[1.1] mb-5" data-testid="newsletter-confirm-success">
                {state.already ? "Bereits bestätigt — danke!" : "Vielen Dank — Anmeldung bestätigt!"}
              </h1>
              <p className="text-lg text-neutral-700 leading-relaxed max-w-2xl mb-3">
                {state.already
                  ? "Diese E-Mail-Adresse war bereits für unseren Newsletter freigeschaltet. Es ist nichts weiter zu tun."
                  : "Ihre E-Mail-Adresse ist jetzt für unseren Newsletter freigeschaltet. Sie erhalten künftig Neuigkeiten, Hinweise und wichtige Informationen rund um das Quartier am Hornschen Tor direkt in Ihr Postfach."}
              </p>
              {state.email && (
                <p className="text-sm text-neutral-500 mb-8" data-testid="newsletter-confirm-email">
                  Bestätigte Adresse: <span className="text-neutral-800 font-medium">{state.email}</span>
                </p>
              )}
              <div className="flex flex-wrap gap-3 mt-2">
                <Link
                  to="/"
                  data-testid="newsletter-confirm-back-home"
                  className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white pl-6 pr-5 py-3.5 rounded-full hover:bg-[hsl(var(--brand-green-strong))] transition-colors text-sm font-medium"
                >
                  Zur Startseite <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 border border-brand-line bg-white text-neutral-800 px-5 py-3.5 rounded-full hover:bg-neutral-50 transition-colors text-sm font-medium"
                >
                  Aktuelle News ansehen
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-white border-2 border-neutral-300 mb-6">
                <XCircle className="h-7 w-7 text-neutral-600" strokeWidth={1.8} />
              </div>
              <h1 className="font-semibold text-4xl sm:text-5xl leading-[1.1] mb-5" data-testid="newsletter-confirm-error">
                Bestätigung nicht möglich
              </h1>
              <p className="text-lg text-neutral-700 leading-relaxed max-w-2xl mb-8">
                {state.message}
              </p>
              <Link
                to="/#newsletter"
                className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white pl-6 pr-5 py-3.5 rounded-full hover:bg-[hsl(var(--brand-green-strong))] transition-colors text-sm font-medium"
              >
                Erneut anmelden <ArrowRight className="h-4 w-4" />
              </Link>
            </>
          )}
        </div>

        <div className="absolute inset-x-0 bottom-0 pointer-events-none select-none">
          <img
            src={SKYLINE_URL}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="w-full h-auto block opacity-90"
          />
        </div>
      </section>
    </div>
  );
}
